import React, { useEffect, useMemo, useRef, useState } from "react";
import "./style.scss";
import { adjustReferenceTime } from "components/fragments/dashboard/utils";
import { T } from "services/lang/LanguageService";
import { useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { InputNumber } from "primereact/inputnumber";
import Timeline from "./Timeline";
import {
  HeatMapDataset,
  ICalendarHeatmapProps,
} from "./calendar-heatmap-editor/CalendarHeatmapEditor";

enum ScreenSizes {
  LARGE = "large",
  MEDIUM = "medium",
  SMALL = "small",
  EXTRA_SMALL = "extra-small",
}

const CalendarHeatmap = (props: ICalendarHeatmapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [screenSize, setScreenSize] = useState<ScreenSizes>(ScreenSizes.LARGE);
  const generatedDates = adjustReferenceTime(props.loadConf);
  const dateFrom =
    props.useLoadConfig && generatedDates.dateFrom
      ? new Date(generatedDates.dateFrom)
      : new Date(props.startDate);
  const dateTo =
    props.useLoadConfig && generatedDates.dateTo
      ? new Date(new Date(generatedDates.dateTo).getTime() - 60 * 1000)
      : new Date(props.endDate);

  const [currentStartDate, setCurrentStartDate] = useState<Date>(dateFrom);
  const [currentEndDate, setCurrentEndDate] = useState<Date>(dateTo);
  const [highValue, setHighValue] = useState<number | null | undefined>(props.maxValue);

  const { data: dataset, isLoading } = useGetDataInfoRange(
    props.identifier ? [props.identifier] : [],
    {
      timeStart: currentStartDate.toISOString(),
      timeEnd: currentEndDate.toISOString(),
    },
    {
      enabled: !props.isCustomDataset && !!props.identifier,
    }
  );

  const minMaxValues = useMemo(() => {
    if (props.isCustomDataset && props.dataset) {
      const values = props.dataset.map((row: HeatMapDataset) => row.value);
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);
      return [minValue, maxValue];
    }
    if (dataset && dataset[0] && dataset[0].data.length > 0) {
      const values = dataset[0].data.map(row => row.value);
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);
      return [minValue, maxValue];
    }
    return undefined;
  }, [dataset, props.dataset, props.isCustomDataset]);

  const startDate = props.isCustomDataset ? props.startDate : new Date(currentStartDate);
  const endDate = props.isCustomDataset ? props.endDate : new Date(currentEndDate);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        if (containerRef.current.offsetWidth >= 1420) {
          setScreenSize(ScreenSizes.LARGE);
        } else if (
          containerRef.current.offsetWidth >= 1140 &&
          containerRef.current.offsetWidth < 1420
        ) {
          setScreenSize(ScreenSizes.MEDIUM);
        } else if (
          containerRef.current.offsetWidth >= 880 &&
          containerRef.current.offsetWidth < 1140
        ) {
          setScreenSize(ScreenSizes.SMALL);
        } else {
          setScreenSize(ScreenSizes.EXTRA_SMALL);
        }
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const colorFunc = (value: number) => {
    const maxValue = highValue ?? minMaxValues?.[1] ?? 0;
    const minValue = props.minValue ?? minMaxValues?.[0] ?? 0;
    let currentValue = value;
    if (value > maxValue) currentValue = maxValue;
    if (value < minValue) currentValue = minValue;
    const ratio = (currentValue - minValue) / (maxValue - minValue);
    const minColor = parseColor(props.lowValueColor ? props.lowValueColor : "#EF553B");
    const maxColor = parseColor(props.highValueColor ? props.highValueColor : "#636EFA");
    const r = Math.round(minColor.r * (1 - ratio) + maxColor.r * ratio);
    const g = Math.round(minColor.g * (1 - ratio) + maxColor.g * ratio);
    const b = Math.round(minColor.b * (1 - ratio) + maxColor.b * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const gradientColors = useMemo(() => {
    return Array.from({ length: 10 }, (_, i) => {
      const ratio = i / 9;
      const minColor = parseColor(props.lowValueColor ? props.lowValueColor : "#EF553B");
      const maxColor = parseColor(props.highValueColor ? props.highValueColor : "#636EFA");
      const r = Math.round(minColor.r * (1 - ratio) + maxColor.r * ratio);
      const g = Math.round(minColor.g * (1 - ratio) + maxColor.g * ratio);
      const b = Math.round(minColor.b * (1 - ratio) + maxColor.b * ratio);
      return `rgb(${r}, ${g}, ${b})`;
    });
  }, [props.lowValueColor, props.highValueColor]);

  const yearsInSelectedRange = Array.from(
    { length: endDate.getFullYear() - startDate.getFullYear() + 1 },
    (_, i) => startDate.getFullYear() + i
  );

  const getRangeForYear = (year: number): [Date, Date] => {
    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    if (year === startDate.getFullYear() && year === endDate.getFullYear()) {
      return [startDate, endDate];
    }
    if (year === startDate.getFullYear()) {
      return [startDate, yearEnd];
    }
    if (year === endDate.getFullYear()) {
      return [yearStart, endDate];
    }
    return [yearStart, yearEnd];
  };

  const renderGradientBar = () => {
    if (props.staticMinMaxTitles) {
      return (
        <>
          <div>{props.lowValueTitle}</div>
          {gradientColors.map(color => (
            <div key={color} className="gradient-box" style={{ backgroundColor: color }} />
          ))}
          <div>{props.highValueTitle}</div>
        </>
      );
    }

    return (
      <>
        <div>{props.minValue ?? (minMaxValues && minMaxValues[0])}</div>
        {gradientColors.map(color => (
          <div key={color} className="gradient-box" style={{ backgroundColor: color }} />
        ))}
        <InputNumber
          value={highValue}
          onChange={e => setHighValue(e.value)}
          inputClassName="border-none focus:shadow-none"
        />
      </>
    );
  };

  const data = props.isCustomDataset
    ? props.dataset
    : dataset?.[0]?.data.map(item => ({
        date: new Date(item.key),
        value: item.value,
      }));

  return (
    <div ref={containerRef} className={`container ${screenSize}`}>
      {!props.hideHeader && (
        <div
          className={`header-container ${screenSize === ScreenSizes.EXTRA_SMALL ? ScreenSizes.EXTRA_SMALL : "normal-sizes"}`}
        >
          {props.showDateSelection && (
            <div className="flex flex-row gap-2">
              <CalendarWrapper
                className="h-3rem"
                value={startDate}
                onChange={props.isCustomDataset ? props.setStartDate! : setCurrentStartDate}
                dateFormat="yy-mm-dd"
              />
              <CalendarWrapper
                className="h-3rem"
                value={endDate}
                onChange={props.isCustomDataset ? props.setEndDate! : setCurrentEndDate}
                dateFormat="yy-mm-dd"
              />
            </div>
          )}
          <div className="flex flex-row gradient-bar">{renderGradientBar()}</div>
          <div
            className={`flex ${props.showDateSelection && screenSize !== ScreenSizes.EXTRA_SMALL ? "flex-column" : "flex-row"} gap-2`}
          >
            <div className="flex flex-row gap-2">
              <div className="planned-status">.</div>
              <div>{T("word.future")}</div>
            </div>
            <div className="flex flex-row gap-2">
              <div className="no-status" />
              <div>{T("word.no-status")}</div>
            </div>
          </div>
        </div>
      )}
      {((!isLoading && data && data.length > 0) ||
        (props.isCustomDataset && props.dataset && props.dataset.length > 0)) &&
        yearsInSelectedRange.length > 0 && (
          <div>
            {yearsInSelectedRange.map(year => (
              <div
                key={year}
                className={
                  screenSize === ScreenSizes.EXTRA_SMALL ? `flex flex-column` : `flex flex-row`
                }
              >
                <div
                  className={`timeline-text ${screenSize === ScreenSizes.EXTRA_SMALL ? ScreenSizes.EXTRA_SMALL : "normal-sizes"}`}
                >
                  {year}
                </div>
                <Timeline
                  range={getRangeForYear(year)}
                  data={
                    data
                      ? data.map((item: HeatMapDataset) => ({
                          date: new Date(item.date),
                          value: item.value,
                        }))
                      : []
                  }
                  colorFunc={colorFunc}
                  ignoreCurrentDay={props.ignoreCurrentDay}
                />
              </div>
            ))}
          </div>
        )}
      {isLoading && !props.isCustomDataset && <div>{T("word.loading")}</div>}
      {!isLoading && !dataset && !props.isCustomDataset && !props.dataset && (
        <div>{T("message.selected-dataset-not-found")}</div>
      )}
    </div>
  );
};

const parseColor = (hex: string) => {
  let modifiedHex = hex;
  if (!modifiedHex) return { r: 0, g: 0, b: 0 };
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  modifiedHex = modifiedHex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(modifiedHex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
};

export default CalendarHeatmap;
